import { useMemo, useState } from "react";
import { Platform, Pressable, Text, View } from "react-native";
import {
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Dna,
  FlaskConical,
  TestTubes,
  type LucideIcon,
} from "lucide-react-native";
import {
  CompactActionButton,
  SectionTitle,
  cardSurface,
} from "@louise/moon-ui";
import { EMBER, colors, semanticColors } from "@louise/moon-ui/theme/tokens";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { calculateBasicInfoCompletion } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { calculateInfertilityCompletion } from "@src/packages/medical-history/fertility/infertility/calculateInfertilityCompletion";
import { calculateFertilityTestingCompletion } from "@src/packages/medical-history/fertility/fertility-testing/calculateFertilityTestingCompletion";
import { calculateFertilityTreatmentsCompletion } from "@src/packages/medical-history/fertility/fertility-treatments/calculateFertilityTreatmentsCompletion";
import { calculateMedicationsVaccinesCompletion } from "@src/packages/medical-history/medical/medications-vaccines/calculateMedicationsVaccinesCompletion";
import { calculateAllergiesCompletion } from "@src/packages/medical-history/medical/allergies/calculateAllergiesCompletion";
import { calculateSurgeriesCompletion } from "@src/packages/medical-history/medical/surgeries/calculateSurgeriesCompletion";
import { calculateDietSupplementsCompletion } from "@louise/medical-history/lifestyle/diet-supplements/calculateDietSupplementsCompletion";
import { calculateExposureCompletion } from "@louise/medical-history/lifestyle/exposure/calculateExposureCompletion";
import { calculateSubstancesCompletion } from "@louise/medical-history/lifestyle/substances/calculateSubstancesCompletion";
import { calculateSleepActivityCompletion } from "@louise/medical-history/lifestyle/sleep-activity/calculateSleepActivityCompletion";
import { calculateMenstrualHealthCompletion } from "@src/packages/medical-history/gynae/menstrual-health/calculateMenstrualHealthCompletion";
import { calculateDiseaseHistoryCompletion } from "@src/packages/medical-history/medical/disease-history/calculateDiseaseHistoryCompletion";
import { calculateSexualHealthCompletion } from "@src/packages/medical-history/gynae/sexual-health/calculateSexualHealthCompletion";
import { calculateContraceptionCompletion } from "@src/packages/medical-history/gynae/contraception/calculateContraceptionCompletion";
import { calculatePregnancyCompletion } from "@src/packages/medical-history/gynae/pregnancies/calculatePregnancyCompletion";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  estimateEggsFromAgeAmhAfc,
  estimateEggsFromAgeAndAmh,
  estimateEggsFromFullInputs,
} from "@src/packages/homepage/utils/eggSuccessCalculator";
import {
  useOvarianReserveInputs,
  type ReserveInputs,
} from "@src/packages/homepage/utils/ovarianReserveInputs";
import type { PlanAction } from "../plan.types";
import { getJourneyStepCompletionId } from "../completion";
import { BiologyIllustration } from "./EggFreezingBiologyCarouselCard";
import { HorizontalSnapCarousel } from "./HorizontalSnapCarousel";
import MedicalHistoryStepCard from "./MedicalHistoryStepCard";
import { NeutralStepPill } from "./NeutralStepPill";
import { OrganicMiniIcon, type OrganicMiniIconKind } from "./OrganicMiniIcon";
import {
  TimelineActionRow,
  TimelineSubtaskCard,
} from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";
import { TestingOptionCard } from "./TestingOptionCard";

const TOTAL_HISTORY_SECTIONS = 16;

type Props = {
  userAge?: number;
  onAction: (action: PlanAction) => void;
};

const ESTIMATE_NS = "homepage.egg_freezing_know_your_numbers";

function estimateForAge(t: TranslationFunction, userAge?: number) {
  if (!userAge) {
    return {
      value: t(`${ESTIMATE_NS}.estimate_none_value`),
      body: t(`${ESTIMATE_NS}.estimate_none_body`),
    };
  }

  if (userAge < 35) {
    return {
      value: t("homepage.shared.often_10_20_eggs"),
      body: t(`${ESTIMATE_NS}.estimate_under_35_body`),
    };
  }

  if (userAge <= 37) {
    return {
      value: t(`${ESTIMATE_NS}.estimate_8_14`),
      body: t(`${ESTIMATE_NS}.estimate_35_37_body`),
    };
  }

  if (userAge <= 40) {
    return {
      value: t(`${ESTIMATE_NS}.estimate_5_10`),
      body: t(`${ESTIMATE_NS}.estimate_37_40_body`),
    };
  }

  return {
    value: t(`${ESTIMATE_NS}.estimate_clinic_specific`),
    body: t(`${ESTIMATE_NS}.estimate_over_40_body`),
  };
}

function estimateForInputs(
  t: TranslationFunction,
  userAge: number | undefined,
  inputs: ReserveInputs,
): { value: string; body: string } {
  if (
    userAge &&
    inputs.amh &&
    inputs.afc !== undefined &&
    inputs.fsh &&
    userAge >= 18 &&
    userAge <= 50
  ) {
    try {
      const full = estimateEggsFromFullInputs(
        userAge,
        inputs.amh.value,
        inputs.amh.unit,
        inputs.afc,
        inputs.fsh.value,
        inputs.fsh.unit,
      );
      return {
        value: t(`${ESTIMATE_NS}.estimate_full_value`, {
          params: { low: String(full.low), high: String(full.high) },
        }),
        body: t(`${ESTIMATE_NS}.estimate_full_body`),
      };
    } catch {
      // fall through to lower tier
    }
  }

  if (
    userAge &&
    inputs.amh &&
    inputs.afc !== undefined &&
    userAge >= 18 &&
    userAge <= 50
  ) {
    try {
      const est = estimateEggsFromAgeAmhAfc(
        userAge,
        inputs.amh.value,
        inputs.amh.unit,
        inputs.afc,
      );
      return {
        value: t(`${ESTIMATE_NS}.estimate_amh_afc_value`, {
          params: { low: String(est.low), high: String(est.high) },
        }),
        body: t(`${ESTIMATE_NS}.estimate_amh_afc_body`),
      };
    } catch {
      // fall through to next tier
    }
  }

  if (userAge && inputs.amh) {
    const est = estimateEggsFromAgeAndAmh(
      userAge,
      inputs.amh.value,
      inputs.amh.unit,
    );
    const reserve = est.statusLabel.toLowerCase();
    return {
      value: t(`${ESTIMATE_NS}.estimate_amh_value`, {
        params: { low: String(est.low), high: String(est.high) },
      }),
      body: t(`${ESTIMATE_NS}.estimate_amh_body`, {
        params: { reserve },
      }),
    };
  }

  return estimateForAge(t, userAge);
}

function useMedicalHistoryProgress() {
  const { data: patient, isLoading } = usePatient();

  return useMemo(() => {
    if (isLoading || !patient) {
      return {
        isLoading,
        progressPercent: 0,
        completedSections: 0,
        isComplete: false,
        fertilityTestingPercent: 0,
      };
    }

    const completionSections = [
      calculateBasicInfoCompletion(patient),
      calculateInfertilityCompletion(patient),
      calculateFertilityTestingCompletion(patient),
      calculateFertilityTreatmentsCompletion(patient),
      calculateMenstrualHealthCompletion(patient),
      calculateSexualHealthCompletion(patient),
      calculateContraceptionCompletion(patient),
      calculatePregnancyCompletion(patient),
      calculateMedicationsVaccinesCompletion(patient),
      calculateAllergiesCompletion(patient),
      calculateSurgeriesCompletion(patient),
      calculateDiseaseHistoryCompletion(patient),
      calculateExposureCompletion(patient),
      calculateSubstancesCompletion(patient),
      calculateSleepActivityCompletion(patient),
      calculateDietSupplementsCompletion(patient),
    ];

    const completedSections = completionSections.filter(
      (section) => section.percent >= 0.999,
    ).length;
    const progressPercent = Math.round(
      (completedSections / TOTAL_HISTORY_SECTIONS) * 100,
    );
    const fertilityTesting = calculateFertilityTestingCompletion(patient);

    return {
      isLoading: false,
      progressPercent,
      completedSections,
      isComplete: completedSections >= TOTAL_HISTORY_SECTIONS,
      fertilityTestingPercent: Math.round(fertilityTesting.percent * 100),
    };
  }, [isLoading, patient]);
}

function InfoRow({
  icon: Icon,
  title,
  body,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
}) {
  return (
    <View style={{ flexDirection: "row", gap: 10, alignItems: "flex-start" }}>
      <View
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: semanticColors.surface.subtle,
          borderWidth: 1,
          borderColor: semanticColors.border.strong,
        }}
      >
        <Icon size={14} color={EMBER.colors.textLight} strokeWidth={1.75} />
      </View>
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 17,
            color: EMBER.colors.text,
            fontWeight: "700",
          }}
        >
          {title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 18,
            color: EMBER.colors.textMid,
            fontWeight: "400",
            marginTop: 2,
          }}
        >
          {body}
        </Text>
      </View>
    </View>
  );
}

function ActionPill({
  label,
  onPress,
  primary,
}: {
  label: string;
  onPress: () => void;
  primary?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: primary
          ? "transparent"
          : semanticColors.action.quiet.border,
        backgroundColor: primary
          ? pressed
            ? "rgba(92, 52, 32, 0.9)"
            : colors.softBrown
          : pressed
            ? semanticColors.overlay.pressed
            : semanticColors.action.quiet.background,
        paddingHorizontal: 12,
        paddingVertical: 8,
      })}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 15,
          color: primary ? semanticColors.text.inverse : EMBER.colors.text,
          fontWeight: "700",
        }}
      >
        {label}
      </Text>
      <ArrowRight
        size={13}
        color={primary ? semanticColors.text.inverse : EMBER.colors.text}
        strokeWidth={1.8}
      />
    </Pressable>
  );
}

type BaselineActionRow = {
  id: string;
  icon: LucideIcon;
  title: string;
  body: string;
  timing?: string;
  actions: {
    label: string;
    action: PlanAction;
    primary?: boolean;
  }[];
};

function getTestingIconKind(id: string): OrganicMiniIconKind {
  if (id === "amh") return "amh";
  if (id === "afc") return "afc";
  if (id === "hormones") return "hormones";
  if (id === "semen") return "panel";
  if (id === "complete-bloodwork") return "panel";
  if (id === "genome" || id === "carrier") return "genetics";
  return "panel";
}

const KEY = "homepage.review_concept";

function buildBaselineTestRows(t: TranslationFunction): BaselineActionRow[] {
  return [
    {
      id: "history",
      icon: ClipboardList,
      title: t(`${KEY}.test_history_title`),
      body: t(`${KEY}.test_history_body`),
      timing: t(`${KEY}.test_history_timing`),
      actions: [
        {
          label: t(`${KEY}.test_history_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_history_action_label`),
            screen: "MedicalHistory",
          },
        },
      ],
    },
    {
      id: "amh",
      icon: TestTubes,
      title: t(`${KEY}.test_amh_title`),
      body: t(`${KEY}.test_amh_body`),
      timing: t(`${KEY}.test_amh_timing`),
      actions: [
        {
          label: t(`${KEY}.test_amh_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_amh_action_label`),
            screen: "AtHomeFertilityTesting",
          },
        },
        {
          label: t(`${KEY}.test_find_lab_cta`),
          action: {
            label: t(`${KEY}.test_find_lab_action_label`),
            screen: "FindCareScreen",
          },
        },
      ],
    },
    {
      id: "afc",
      icon: Building2,
      title: t(`${KEY}.test_afc_title`),
      body: t(`${KEY}.test_afc_body`),
      timing: t(`${KEY}.test_afc_timing`),
      actions: [
        {
          label: t(`${KEY}.test_afc_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_afc_action_label`),
            screen: "FindCareScreen",
          },
        },
        {
          label: t(`${KEY}.test_upload_result_cta`),
          action: {
            label: t(`${KEY}.test_afc_upload_action_label`),
            signal: "open-document-upload",
            params: { initialCategory: "medical_records" },
          },
        },
      ],
    },
    {
      id: "hormones",
      icon: FlaskConical,
      title: t(`${KEY}.test_hormones_title`),
      body: t(`${KEY}.test_hormones_body`),
      timing: t(`${KEY}.test_hormones_timing`),
      actions: [
        {
          label: t(`${KEY}.test_find_lab_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_find_lab_action_label`),
            screen: "FindCareScreen",
          },
        },
        {
          label: t(`${KEY}.test_add_results_cta`),
          action: {
            label: t(`${KEY}.test_add_results_action_label`),
            signal: "open-document-upload",
            params: { initialCategory: "medical_records" },
          },
        },
      ],
    },
  ];
}

function buildEmbryoFreezingExtraTestRows(
  t: TranslationFunction,
): BaselineActionRow[] {
  return [
    {
      id: "semen",
      icon: FlaskConical,
      title: t(`${KEY}.test_semen_title`),
      body: t(`${KEY}.test_semen_body`),
      timing: t(`${KEY}.test_semen_timing`),
      actions: [
        {
          label: t(`${KEY}.test_check_options_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_semen_check_action_label`),
            screen: "AtHomeFertilityTesting",
          },
        },
        {
          label: t(`${KEY}.test_upload_result_cta`),
          action: {
            label: t(`${KEY}.test_semen_upload_action_label`),
            signal: "open-document-upload",
            params: { initialCategory: "medical_records" },
          },
        },
      ],
    },
  ];
}

function buildIuiTestRows(t: TranslationFunction): BaselineActionRow[] {
  return [
    {
      id: "ovulation",
      icon: CalendarDays,
      title: t(`${KEY}.test_ovulation_title`),
      body: t(`${KEY}.test_ovulation_body`),
      timing: t(`${KEY}.test_ovulation_timing`),
      actions: [
        {
          label: t(`${KEY}.test_check_timing_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_check_timing_action_label`),
            screen: "AtHomeFertilityTesting",
          },
        },
        {
          label: t(`${KEY}.test_find_iui_monitoring_cta`),
          action: {
            label: t(`${KEY}.test_find_iui_monitoring_action_label`),
            screen: "FindCareScreen",
            params: { filter: "iui" },
          },
        },
      ],
    },
    {
      id: "tube-status",
      icon: Building2,
      title: t(`${KEY}.test_tube_title`),
      body: t(`${KEY}.test_tube_body`),
      timing: t(`${KEY}.test_tube_timing`),
      actions: [
        {
          label: t(`${KEY}.test_find_testing_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_find_tube_action_label`),
            screen: "FindCareScreen",
            params: { filter: "iui" },
          },
        },
        {
          label: t(`${KEY}.test_upload_result_cta`),
          action: {
            label: t(`${KEY}.test_upload_tube_action_label`),
            signal: "open-document-upload",
            params: { initialCategory: "medical_records" },
          },
        },
      ],
    },
    {
      id: "semen",
      icon: FlaskConical,
      title: t(`${KEY}.test_semen_iui_title`),
      body: t(`${KEY}.test_semen_iui_body`),
      timing: t(`${KEY}.test_semen_iui_timing`),
      actions: [
        {
          label: t(`${KEY}.test_check_options_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_semen_check_action_label`),
            screen: "AtHomeFertilityTesting",
          },
        },
        {
          label: t(`${KEY}.test_upload_result_cta`),
          action: {
            label: t(`${KEY}.test_semen_upload_action_label`),
            signal: "open-document-upload",
            params: { initialCategory: "medical_records" },
          },
        },
      ],
    },
    {
      id: "clinic-labs",
      icon: TestTubes,
      title: t(`${KEY}.test_clinic_labs_title`),
      body: t(`${KEY}.test_clinic_labs_body`),
      timing: t(`${KEY}.test_clinic_labs_timing`),
      actions: [
        {
          label: t(`${KEY}.test_find_lab_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_find_lab_action_label`),
            screen: "FindCareScreen",
            params: { filter: "iui" },
          },
        },
        {
          label: t(`${KEY}.test_add_results_cta`),
          action: {
            label: t(`${KEY}.test_add_results_action_label`),
            signal: "open-document-upload",
            params: { initialCategory: "medical_records" },
          },
        },
      ],
    },
  ];
}

function buildIvfExtraTestRows(t: TranslationFunction): BaselineActionRow[] {
  return [
    ...buildEmbryoFreezingExtraTestRows(t),
    {
      id: "uterine",
      icon: Building2,
      title: t(`${KEY}.test_uterine_title`),
      body: t(`${KEY}.test_uterine_body`),
      timing: t(`${KEY}.test_uterine_timing`),
      actions: [
        {
          label: t(`${KEY}.test_find_evaluation_cta`),
          primary: true,
          action: {
            label: t(`${KEY}.test_find_uterine_action_label`),
            screen: "FindCareScreen",
            params: { filter: "ivf" },
          },
        },
        {
          label: t(`${KEY}.test_upload_result_cta`),
          action: {
            label: t(`${KEY}.test_upload_uterine_action_label`),
            signal: "open-document-upload",
            params: { initialCategory: "medical_records" },
          },
        },
      ],
    },
  ];
}

type TestingLevelRow = {
  id: string;
  icon: LucideIcon;
  label: string;
  title: string;
  body: string;
  action: {
    label: string;
    planAction: PlanAction;
  };
};

function buildTestingLevelRows(t: TranslationFunction): TestingLevelRow[] {
  return [
    {
      id: "core",
      icon: TestTubes,
      label: t(`${KEY}.test_eyebrow_baseline`),
      title: t(`${KEY}.test_level_core_title`),
      body: t(`${KEY}.test_level_core_body`),
      action: {
        label: t(`${KEY}.test_level_core_cta`),
        planAction: {
          label: t(`${KEY}.test_amh_action_label`),
          screen: "AtHomeFertilityTesting",
        },
      },
    },
    {
      id: "complete-bloodwork",
      icon: FlaskConical,
      label: t(`${KEY}.test_eyebrow_optional`),
      title: t(`${KEY}.test_level_panel_title`),
      body: t(`${KEY}.test_level_panel_body`),
      action: {
        label: t(`${KEY}.test_level_panel_cta`),
        planAction: {
          label: t(`${KEY}.test_level_panel_action_label`),
          screen: "AtHomeFertilityTesting",
        },
      },
    },
    {
      id: "genetics",
      icon: Dna,
      label: t(`${KEY}.test_eyebrow_optional`),
      title: t(`${KEY}.test_level_genetics_title`),
      body: t(`${KEY}.test_level_genetics_body`),
      action: {
        label: t(`${KEY}.test_level_genetics_cta`),
        planAction: {
          label: t(`${KEY}.test_level_genetics_action_label`),
          screen: "AtHomeFertilityTesting",
        },
      },
    },
  ];
}

function MiniActionButton({
  label,
  onPress,
  primary,
}: {
  label: string;
  onPress: () => void;
  primary?: boolean;
}) {
  return (
    <CompactActionButton
      label={label}
      onPress={onPress}
      variant={primary ? "primary" : "secondary"}
      size="sm"
      trailingIcon={ArrowRight}
      iconSize={12}
      iconStrokeWidth={1.8}
    />
  );
}

function TestingLevelOption({
  item,
  index,
  onAction,
}: {
  item: TestingLevelRow;
  index: number;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <View
      style={{
        borderTopWidth: index === 0 ? 0 : 1,
        borderTopColor: semanticColors.border.subtle,
        paddingTop: index === 0 ? 0 : 11,
        gap: 8,
      }}
    >
      <View style={{ flexDirection: "row", gap: 10, alignItems: "flex-start" }}>
        <View
          style={{
            width: 28,
            height: 28,
            borderRadius: 999,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: semanticColors.surface.subtle,
            borderWidth: 1,
            borderColor: semanticColors.border.strong,
          }}
        >
          <OrganicMiniIcon kind={getTestingIconKind(item.id)} size={28} />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              lineHeight: 13,
              color: EMBER.colors.textLight,
              fontWeight: "700",
              letterSpacing: 0.4,
              textTransform: "uppercase",
            }}
          >
            {item.label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 17,
              color: EMBER.colors.text,
              fontWeight: "700",
              marginTop: 2,
            }}
          >
            {item.title}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              fontWeight: "400",
              marginTop: 2,
            }}
          >
            {item.body}
          </Text>
        </View>
      </View>
      <View style={{ paddingLeft: 38 }}>
        <MiniActionButton
          label={item.action.label}
          primary={index === 0}
          onPress={() => onAction(item.action.planAction)}
        />
      </View>
    </View>
  );
}

function BaselineTestRow({
  row,
  index,
  onAction,
}: {
  row: BaselineActionRow;
  index: number;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <View
      style={{
        borderTopWidth: index === 0 ? 0 : 1,
        borderTopColor: semanticColors.border.subtle,
        paddingTop: index === 0 ? 0 : 13,
        gap: 10,
      }}
    >
      <View style={{ flexDirection: "row", gap: 10, alignItems: "flex-start" }}>
        <View
          style={{
            width: 30,
            height: 30,
            borderRadius: 999,
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <OrganicMiniIcon kind={getTestingIconKind(row.id)} size={30} />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 18,
              color: EMBER.colors.text,
              fontWeight: "700",
            }}
          >
            {row.title}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              fontWeight: "400",
              marginTop: 3,
            }}
          >
            {row.body}
          </Text>
          {row.timing ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10,
                lineHeight: 13,
                color: EMBER.colors.textLight,
                fontWeight: "700",
                letterSpacing: 0.35,
                textTransform: "uppercase",
                marginTop: 6,
              }}
            >
              {row.timing}
            </Text>
          ) : null}
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 7,
          paddingLeft: 40,
        }}
      >
        {row.actions.map((item) => (
          <MiniActionButton
            key={item.label}
            label={item.label}
            primary={item.primary}
            onPress={() => onAction(item.action)}
          />
        ))}
      </View>
    </View>
  );
}

type TestOptionCardData = {
  id: string;
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  body: string;
  timing?: string;
  actions: {
    label: string;
    action: PlanAction;
    primary?: boolean;
  }[];
};

function buildTestOptions(
  baselineRows: BaselineActionRow[],
  t: TranslationFunction,
): TestOptionCardData[] {
  const eyebrowFor = (id: string): string => {
    if (id === "amh") return t(`${KEY}.test_eyebrow_ovarian_reserve`);
    if (id === "afc") return t(`${KEY}.test_eyebrow_ultrasound`);
    if (id === "semen") return t(`${KEY}.test_eyebrow_sperm_side`);
    if (id === "ovulation") return t(`${KEY}.test_eyebrow_cycle_timing`);
    if (id === "tube-status") return t(`${KEY}.test_eyebrow_tube_status`);
    if (id === "uterine") return t(`${KEY}.test_eyebrow_transfer_readiness`);
    if (id === "egg-source") return t(`${KEY}.test_eyebrow_egg_source`);
    if (id === "carrier-transfer") {
      return t(`${KEY}.test_eyebrow_carrier_route`);
    }
    if (id === "clinic-labs") return t(`${KEY}.test_eyebrow_clinic_labs`);
    return t(`${KEY}.test_eyebrow_hormones`);
  };
  return [
    ...baselineRows.map((row) => ({
      id: row.id,
      icon: row.icon,
      eyebrow: eyebrowFor(row.id),
      title: row.title,
      body: row.body,
      timing: row.timing,
      actions: row.actions,
    })),
    ...buildTestingLevelRows(t)
      .slice(1)
      .map((row) => ({
        id: row.id,
        icon: row.icon,
        eyebrow: row.label,
        title: row.title,
        body: row.body,
        actions: [
          {
            label: row.action.label,
            action: row.action.planAction,
            primary: false,
          },
        ],
      })),
  ];
}

function TestingOptionCarouselCard({
  item,
  width,
  onAction,
}: {
  item: TestOptionCardData;
  width: number;
  onAction: (action: PlanAction) => void;
}) {
  return (
    <TestingOptionCard
      item={{
        id: item.id,
        eyebrow: item.eyebrow,
        title: item.title,
        body: item.body,
        timing: item.timing,
        actions: item.actions.map((action) => ({
          label: action.label,
          primary: action.primary,
          onPress: () => onAction(action.action),
        })),
      }}
      width={width}
    />
  );
}

type KnowYourNumbersVariant =
  | "egg-freezing"
  | "embryo-freezing"
  | "ivf"
  | "iui"
  | "amab-family-building"
  | "explorer";

type KnowYourNumbersViewerSexAtBirth = "AFAB" | "AMAB" | "unknown";

type TestingPriority = "ovarian-first" | "sperm-first";

function getKnowYourNumbersConfig(
  variant: KnowYourNumbersVariant,
  priority: TestingPriority = "ovarian-first",
  t?: TranslationFunction,
  viewerSexAtBirth: KnowYourNumbersViewerSexAtBirth = "unknown",
) {
  // When `t` is provided, user-facing strings are translated. When omitted,
  // the function falls back to English so existing callers keep working.
  const tx = (key: string, fallback: string) =>
    t ? t(`homepage.review_concept.${key}`) : fallback;
  const baselineRows = t
    ? buildBaselineTestRows(t)
    : ([] as BaselineActionRow[]);
  const embryoExtraRows = t
    ? buildEmbryoFreezingExtraTestRows(t)
    : ([] as BaselineActionRow[]);
  const ivfExtraRows = t
    ? buildIvfExtraTestRows(t)
    : ([] as BaselineActionRow[]);
  const iuiRows = t ? buildIuiTestRows(t) : ([] as BaselineActionRow[]);
  if (variant === "explorer") {
    const rows =
      viewerSexAtBirth === "AMAB"
        ? embryoExtraRows
        : viewerSexAtBirth === "AFAB"
          ? baselineRows.filter((row) =>
              ["amh", "afc", "hormones"].includes(row.id),
            )
          : [
              ...baselineRows.filter((row) => row.id !== "history"),
              ...embryoExtraRows,
            ];

    return {
      stepLabel: tx("numbers_step_label_explorer", "Step 2: Know Your Numbers"),
      rows,
      partnerActionTitle: "",
      partnerActionSubtitle: "",
      subtitle: tx(
        "numbers_subtitle_explorer",
        "Add only the numbers that help you compare paths. The right set depends on what you choose next.",
      ),
    };
  }

  if (variant === "iui") {
    return {
      stepLabel: tx("numbers_step_label_iui", "Step 2: Confirm IUI fit"),
      rows: iuiRows,
      partnerActionTitle: tx(
        "numbers_partner_action_iui_title",
        "Add partner or sperm source",
      ),
      partnerActionSubtitle: tx(
        "numbers_partner_action_iui_subtitle",
        "IUI guidance needs to know whether sperm is partner, donor, fresh, frozen, or still undecided.",
      ),
      subtitle: tx(
        "numbers_subtitle_iui",
        "Confirm the inputs that make IUI work: ovulation timing, tube status, sperm source, and clinic-required labs.",
      ),
    };
  }

  if (variant === "ivf") {
    const ovarianRows = baselineRows.filter((row) => row.id !== "history");
    const rows =
      priority === "sperm-first"
        ? [...embryoExtraRows, ...ovarianRows, ...ivfExtraRows.slice(1)]
        : [...ovarianRows, ...ivfExtraRows];

    return {
      stepLabel: tx("numbers_step_label", "Step 2: Know your numbers"),
      rows,
      partnerActionTitle:
        priority === "sperm-first"
          ? tx(
              "numbers_partner_action_ivf_sperm_title",
              "Add ovarian-side partner, donor, or clinic plan",
            )
          : tx(
              "numbers_partner_action_ivf_title",
              "Add partner, donor, or sperm source",
            ),
      partnerActionSubtitle:
        priority === "sperm-first"
          ? tx(
              "numbers_partner_action_ivf_sperm_subtitle",
              "IVF guidance still needs AMH, AFC, ovarian-side labs, retrieval timing, and transfer route alongside the semen plan.",
            )
          : tx(
              "numbers_partner_action_ivf_subtitle",
              "IVF guidance needs the ovarian side, sperm source, and possible transfer route in one plan.",
            ),
      subtitle:
        priority === "sperm-first"
          ? tx(
              "numbers_subtitle_ivf_sperm",
              "Start with semen analysis and sperm source, while keeping AMH, AFC, ovarian-side labs, and transfer readiness in the same IVF plan.",
            )
          : tx(
              "numbers_subtitle_ivf_ovarian",
              "Start with ovarian reserve, add the sperm side, then account for transfer readiness and clinic-required labs.",
            ),
    };
  }

  if (variant === "amab-family-building") {
    return {
      stepLabel: tx("numbers_step_label_amab", "Step 2: Confirm inputs"),
      rows: [
        ...embryoExtraRows,
        {
          id: "egg-source",
          icon: Dna,
          title: tx("test_amab_egg_title", "Egg source screening"),
          body: tx(
            "test_amab_egg_body",
            "Donor, partner, or known-source eggs change medical screening, genetics, retrieval timing, consent, and shipping.",
          ),
          timing: tx("test_amab_egg_timing", "Partner, known source, or donor"),
          actions: [
            {
              label: tx("test_compare_sources_cta", "Compare sources"),
              primary: true,
              action: {
                label: tx(
                  "test_amab_egg_compare_action_label",
                  "Compare egg source options",
                ),
                screen: "LearnScreen",
                params: { topic: "egg-source" },
              },
            },
            {
              label: tx("test_find_care_cta", "Find care"),
              action: {
                label: tx(
                  "test_amab_egg_find_action_label",
                  "Find donor or IVF care",
                ),
                screen: "FindCareScreen",
                params: { filter: "amab-family-building" },
              },
            },
          ],
        },
        {
          id: "carrier-transfer",
          icon: Building2,
          title: tx("test_amab_carrier_title", "Carrier or transfer path"),
          body: tx(
            "test_amab_carrier_body",
            "If a gestational carrier is involved, legal, agency, clinic, and uterine-readiness steps need to be tracked before embryos are used.",
          ),
          timing: tx(
            "test_amab_carrier_timing",
            "Can run before or after embryo creation",
          ),
          actions: [
            {
              label: tx("test_amab_carrier_review_cta", "Review path"),
              primary: true,
              action: {
                label: tx(
                  "test_amab_carrier_review_action_label",
                  "Review carrier path",
                ),
                screen: "LearnScreen",
                params: { topic: "carrier-path" },
              },
            },
          ],
        },
      ] satisfies BaselineActionRow[],
      partnerActionTitle: tx(
        "numbers_partner_action_amab_title",
        "Add sperm, egg source, and carrier details",
      ),
      partnerActionSubtitle: tx(
        "numbers_partner_action_amab_subtitle",
        "This plan should know whether sperm, egg source, and carrier route are confirmed, still being screened, or undecided.",
      ),
      subtitle: tx(
        "numbers_subtitle_amab",
        "Start with semen analysis, then connect egg source, donor or carrier requirements, genetics, and clinic-required labs.",
      ),
    };
  }

  const isEmbryoFreezing = variant === "embryo-freezing";
  return {
    stepLabel: tx("numbers_step_label", "Step 2: Know your numbers"),
    rows: [
      ...baselineRows.filter((row) => row.id !== "history"),
      ...(isEmbryoFreezing ? embryoExtraRows : []),
    ],
    partnerActionTitle: tx(
      "numbers_partner_action_default_title",
      "Add partner or sperm source",
    ),
    partnerActionSubtitle: tx(
      "numbers_partner_action_default_subtitle",
      "Invite a partner or note whose sperm source should be included in the plan.",
    ),
    subtitle: isEmbryoFreezing
      ? tx(
          "numbers_subtitle_embryo_freezing",
          "Start with the ovarian side, add the sperm side, then decide whether genetics or broader panels are useful.",
        )
      : tx(
          "numbers_subtitle_egg_freezing",
          "A few options can make your estimate more useful. You can start simple or go deeper.",
        ),
  };
}

export function EggFreezingKnowYourNumbersTimelineStepSectionCard({
  onAction,
  showRail = true,
  variant = "egg-freezing",
  priority = "ovarian-first",
  viewerSexAtBirth = "unknown",
  viewerIsSupporting = false,
  cardTitle,
  cardSubtitle,
}: {
  onAction: (action: PlanAction) => void;
  showRail?: boolean;
  variant?: KnowYourNumbersVariant;
  priority?: TestingPriority;
  viewerSexAtBirth?: KnowYourNumbersViewerSexAtBirth;
  /**
   * A partner reading the patient's plan. Inviting a partner or naming the
   * sperm source is the patient's to do, and on an egg freezing plan the
   * numbers are hers rather than the reader's.
   */
  viewerIsSupporting?: boolean;
  cardTitle?: string;
  cardSubtitle?: string;
}) {
  const t = useTranslations();
  const config = getKnowYourNumbersConfig(
    variant,
    priority,
    t,
    viewerSexAtBirth,
  );
  const needsSourceAction =
    !viewerIsSupporting && !["egg-freezing", "explorer"].includes(variant);
  const speaksAboutThePatientsNumbers =
    viewerIsSupporting && variant === "egg-freezing";
  const testOptions = buildTestOptions(config.rows, t);

  return (
    <StepperSection
      id={`precycle-${variant}.know-your-numbers.timeline`}
      stepLabel={
        speaksAboutThePatientsNumbers
          ? t("homepage.review_concept.supporting.know_the_numbers")
          : config.stepLabel
      }
      completionStepId={
        variant === "egg-freezing"
          ? getJourneyStepCompletionId("egg-freezing-solo", "test")
          : "know-your-numbers"
      }
      showRail={showRail}
    >
      <MedicalHistoryStepCard
        stepNumber={2}
        totalSteps={3}
        onAction={onAction}
        showStepPill={false}
        bottomMargin={0}
      />

      {needsSourceAction ? (
        <TimelineActionRow
          title={config.partnerActionTitle}
          subtitle={config.partnerActionSubtitle}
          icon={<OrganicMiniIcon kind="process" size={34} />}
          onPress={() =>
            onAction({
              label: "Add partner or sperm source",
              signal: "open-partner-or-donor-modal",
            })
          }
        />
      ) : null}

      <TimelineSubtaskCard
        id={`precycle-${variant}.know-your-numbers.timeline.what-you-can-test`}
        title={
          cardTitle ??
          (speaksAboutThePatientsNumbers
            ? t("homepage.shared.supporting.what_can_be_tested")
            : t("homepage.review_concept.numbers_what_to_test"))
        }
        subtitle={cardSubtitle ?? config.subtitle}
        titleIcon={<OrganicMiniIcon kind="panel" size={34} />}
        collapsedPreviewHeight={68}
        defaultExpanded
      >
        <HorizontalSnapCarousel
          items={testOptions}
          gap={10}
          fallbackCardWidth={246}
          getCardWidth={(containerWidth) =>
            Math.min(270, Math.max(236, containerWidth * 0.82))
          }
          keyExtractor={(item) => item.id}
          renderItem={(item, width) => (
            <TestingOptionCarouselCard
              item={item}
              width={width}
              onAction={onAction}
            />
          )}
          contentContainerStyle={{ paddingRight: 2 }}
        />
      </TimelineSubtaskCard>
    </StepperSection>
  );
}

export function EggFreezingKnowYourNumbersChecklistCard({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture(
    "EggFreezingKnowYourNumbersChecklistCard",
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const history = useMedicalHistoryProgress();
  const completedCount =
    (history.isComplete ? 1 : 0) +
    (history.fertilityTestingPercent >= 100 ? 3 : 0);
  const isComplete = completedCount >= 4;

  return (
    <View style={{ marginBottom: 16 }}>
      <View style={{ paddingHorizontal: 2, marginBottom: 8 }}>
        <NeutralStepPill
          label={t("homepage.review_concept.numbers_step_label")}
        />
      </View>

      <View
        style={{
          marginBottom: isCollapsed ? 0 : 12,
          paddingHorizontal: 2,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                isCollapsed
                  ? "Expand baseline tests"
                  : "Collapse baseline tests",
              ),
            );
            setIsCollapsed((current) => !current);
          }}
          accessibilityRole="button"
          accessibilityLabel={
            isCollapsed ? "Expand baseline tests" : "Collapse baseline tests"
          }
          style={{ flex: 1 }}
        >
          <SectionTitle icon={TestTubes}>Get baseline tests</SectionTitle>
        </Pressable>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                isCollapsed
                  ? "Expand baseline tests"
                  : "Collapse baseline tests",
              ),
            );
            setIsCollapsed((current) => !current);
          }}
          accessibilityRole="button"
          accessibilityLabel={
            isCollapsed ? "Expand baseline tests" : "Collapse baseline tests"
          }
          style={({ pressed }) => ({
            flexDirection: "row",
            alignItems: "center",
            gap: 5,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: semanticColors.action.quiet.border,
            backgroundColor: pressed
              ? semanticColors.action.quiet.pressedBackground
              : semanticColors.action.quiet.background,
            paddingHorizontal: 9,
            paddingVertical: 5,
          })}
        >
          {isComplete ? (
            <CheckCircle2
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          ) : null}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              lineHeight: 13,
              color: EMBER.colors.textLight,
              fontWeight: "700",
              letterSpacing: 0.3,
              textTransform: "uppercase",
            }}
          >
            {isComplete ? "Done" : `${completedCount} of 4`}
          </Text>
          {isCollapsed ? (
            <ChevronDown
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          ) : (
            <ChevronUp
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          )}
        </Pressable>
      </View>

      {!isCollapsed ? (
        <View
          style={{
            ...cardSurface(),
            padding: 16,
            overflow: "hidden",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 17,
              lineHeight: 22,
              color: EMBER.colors.text,
              fontWeight: "700",
            }}
          >
            Decide how much testing you want right now
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 19,
              color: EMBER.colors.textMid,
              fontWeight: "400",
              marginTop: 6,
            }}
          >
            Start with what predicts cycle response. Add broader bloodwork or
            genetics only if you want more health context before treatment.
          </Text>

          <View
            style={{
              marginTop: 14,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: semanticColors.border.glass,
              backgroundColor: semanticColors.surface.subtle,
              padding: 12,
              ...(Platform.OS === "web"
                ? {
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                  }
                : null),
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10,
                lineHeight: 13,
                color: EMBER.colors.textLight,
                fontWeight: "700",
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              Testing levels
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                lineHeight: 19,
                color: EMBER.colors.text,
                fontWeight: "700",
                marginTop: 4,
              }}
            >
              Baseline first. Deeper testing is optional.
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textMid,
                fontWeight: "400",
                marginTop: 3,
              }}
            >
              The baseline path answers the egg-freezing estimate question. Full
              blood panels and genetics answer broader health or future
              family-building questions.
            </Text>
            <View style={{ gap: 11, marginTop: 12 }}>
              {buildTestingLevelRows(t).map((item, index) => (
                <TestingLevelOption
                  key={item.id}
                  item={item}
                  index={index}
                  onAction={onAction}
                />
              ))}
            </View>
          </View>

          <View style={{ gap: 13, marginTop: 16 }}>
            {buildBaselineTestRows(t).map((row, index) => (
              <BaselineTestRow
                key={row.id}
                row={row}
                index={index}
                onAction={onAction}
              />
            ))}
          </View>

          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 9,
              marginTop: 16,
              padding: 11,
              borderRadius: 12,
              backgroundColor: semanticColors.surface.glass,
              borderWidth: 1,
              borderColor: semanticColors.border.glass,
            }}
          >
            <CalendarDays
              size={15}
              color={EMBER.colors.textLight}
              strokeWidth={1.75}
              style={{ marginTop: 1 }}
            />
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textMid,
                fontWeight: "400",
              }}
            >
              If you already have recent AMH, AFC, or hormone results, upload
              them here. A clinic may repeat baseline testing before treatment,
              but recent results can still help you understand your likely
              response now.
            </Text>
          </View>
        </View>
      ) : null}
    </View>
  );
}

export function EggFreezingKnowYourNumbersCard({ userAge, onAction }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("EggFreezingKnowYourNumbersCard");
  const [expandedWhenComplete, setExpandedWhenComplete] = useState(false);
  const history = useMedicalHistoryProgress();
  const reserveInputs = useOvarianReserveInputs();
  const estimate = estimateForInputs(t, userAge, reserveInputs);
  const isExpanded = !history.isComplete || expandedWhenComplete;

  return (
    <View style={{ marginBottom: 16 }}>
      <Pressable
        onPress={() => {
          if (history.isComplete) {
            capturePress(
              pressLabel(
                expandedWhenComplete
                  ? "collapse_know_your_numbers"
                  : "expand_know_your_numbers",
              ),
            );
            setExpandedWhenComplete((current) => !current);
          }
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          paddingHorizontal: 2,
          gap: 12,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 7,
            flex: 1,
          }}
        >
          {history.isComplete ? (
            <CheckCircle2
              size={13}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          ) : null}
          <NeutralStepPill
            label={t("homepage.review_concept.numbers_step_label")}
          />
        </View>
        {history.isComplete ? (
          expandedWhenComplete ? (
            <ChevronUp
              size={14}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          ) : (
            <ChevronDown
              size={14}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          )
        ) : null}
      </Pressable>

      {isExpanded ? (
        <View
          style={{
            ...cardSurface(),
            padding: 16,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 18,
                  lineHeight: 23,
                  color: EMBER.colors.text,
                  fontWeight: "700",
                }}
              >
                Turn a rough estimate into a real plan
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  lineHeight: 19,
                  color: EMBER.colors.textMid,
                  fontWeight: "400",
                  marginTop: 6,
                }}
              >
                The most useful next step is making your history complete, then
                adding the ovarian reserve numbers that shape egg-freezing
                planning.
              </Text>
            </View>
            <View style={{ transform: [{ scale: 0.72 }], marginRight: -18 }}>
              <BiologyIllustration kind="reserve" />
            </View>
          </View>

          <View
            style={{
              marginTop: 14,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: semanticColors.border.glass,
              backgroundColor: semanticColors.surface.subtle,
              padding: 12,
              ...(Platform.OS === "web"
                ? {
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                  }
                : null),
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10,
                lineHeight: 13,
                color: EMBER.colors.textLight,
                fontWeight: "700",
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              Personalized estimate
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 17,
                lineHeight: 21,
                color: EMBER.colors.text,
                fontWeight: "700",
                marginTop: 4,
              }}
            >
              {estimate.value}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textMid,
                fontWeight: "400",
                marginTop: 4,
              }}
            >
              {estimate.body}
            </Text>
          </View>

          <View style={{ gap: 12, marginTop: 14 }}>
            <InfoRow
              icon={ClipboardList}
              title={`Medical history ${history.progressPercent}% complete`}
              body="This has to stay visible until it is complete because medications, surgeries, allergies, cycles, and diagnoses change what a clinic needs to know."
            />
            <InfoRow
              icon={TestTubes}
              title={`Core fertility numbers${history.fertilityTestingPercent > 0 ? ` ${history.fertilityTestingPercent}% added` : ""}`}
              body="AMH and antral follicle count are the two numbers that most improve an egg-freezing estimate before you pick a protocol."
            />
            <InfoRow
              icon={FlaskConical}
              title="Optional broader blood panel"
              body="FSH, estradiol, LH, progesterone, TSH, prolactin, vitamin D, and similar labs can add context, but they are not all required just to explore."
            />
            <InfoRow
              icon={Building2}
              title="Clinic baseline testing can happen later"
              body="Most clinics repeat baseline labs and ultrasound before treatment. Doing everything through the clinic can be easier, but sometimes costs more."
            />
          </View>

          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 16,
            }}
          >
            <ActionPill
              primary
              label={
                history.isComplete
                  ? "Review medical history"
                  : "Complete medical history"
              }
              onPress={() =>
                onAction({
                  label: "Complete medical history",
                  screen: "MedicalHistory",
                })
              }
            />
            <ActionPill
              label="Add test results"
              onPress={() =>
                onAction({
                  label: "Add test results",
                  signal: "open-document-upload",
                  params: { initialCategory: "medical_records" },
                })
              }
            />
          </View>
        </View>
      ) : (
        <View
          style={{
            ...cardSurface(),
            padding: 14,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              lineHeight: 18,
              color: EMBER.colors.textMid,
              fontWeight: "400",
            }}
          >
            Your history is complete. Reopen this if you want to add or review
            fertility testing.
          </Text>
          <CheckCircle2
            size={18}
            color={EMBER.colors.textLight}
            strokeWidth={1.8}
          />
        </View>
      )}
    </View>
  );
}
